/* sections/sb-sparkblog.liquid: topic tiles, "Show all" and the pager load the matching
   article list in place through the Section Rendering API instead of reloading the page.
   Every control is a real link (/blogs/<blog>/tagged/<tag>, ?page=N), so without JavaScript,
   or if a fetch fails, each one is an ordinary page load. Only [data-sb-blog-latest] is
   swapped; the topic tiles keep their DOM (and focus) and just take the new href/aria-current.

   The theme editor re-renders a section after every settings change and does not re-run its
   scripts, so roots are (re)bound on `shopify:section:load` too. Binding is idempotent per
   element, and one shared popstate handler serves whichever roots are on the page. */
(() => {
  if (window.sbSparkblogLoaded) return;
  window.sbSparkblogLoaded = true;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function init(root) {
    if (root.dataset.sbBlogReady) return;
    root.dataset.sbBlogReady = 'true';

    const sectionId = root.dataset.sectionId;
    const status = root.querySelector('[data-sb-blog-status]');
    let controller = null;
    let navigated = false;

    const currentList = () => root.querySelector('[data-sb-blog-latest]');

    function syncTopics(doc) {
      const fresh = doc.querySelectorAll('[data-sb-blog-topic]');
      root.querySelectorAll('[data-sb-blog-topic]').forEach((tile, index) => {
        const match = fresh[index];
        if (!match) return;
        tile.setAttribute('href', match.getAttribute('href'));
        if (match.hasAttribute('aria-current')) tile.setAttribute('aria-current', 'true');
        else tile.removeAttribute('aria-current');
      });
    }

    function announce(list) {
      if (!status) return;
      const count = Number(list.dataset.count || 0);
      const noun = count === 1 ? 'article' : 'articles';
      status.textContent = list.dataset.label
        ? `Showing ${count} ${list.dataset.label} ${noun}`
        : `Showing all ${noun}`;
    }

    function bringIntoView(list) {
      const top = list.getBoundingClientRect().top;
      if (top < 0 || top > window.innerHeight * 0.6) {
        list.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
      }
    }

    async function load(url, { push = true, focusHeading = false } = {}) {
      const list = currentList();
      if (!list || !sectionId) {
        window.location.href = url;
        return;
      }
      if (controller) controller.abort();
      controller = new AbortController();
      list.setAttribute('aria-busy', 'true');
      try {
        const request = new URL(url, window.location.href);
        request.searchParams.set('section_id', sectionId);
        const response = await fetch(request, { signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const next = doc.querySelector('[data-sb-blog-latest]');
        if (!next) throw new Error('No article list in the response');
        list.replaceWith(next);
        syncTopics(doc);
        if (push) {
          window.history.pushState({ sbBlog: true }, '', url);
          navigated = true;
        }
        if (next.dataset.title) document.title = next.dataset.title;
        announce(next);
        if (focusHeading) next.querySelector('h2')?.focus({ preventScroll: true });
        bringIntoView(next);
      } catch (error) {
        if (error.name === 'AbortError') return;
        window.location.href = url;
      }
    }

    root.addEventListener('click', (event) => {
      const link = event.target.closest('a[data-sb-blog-nav]');
      if (!link || event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      // Keep focus on a tile the visitor just pressed; move it to the heading for
      // "Show all" and the pager, whose links are replaced along with the list.
      load(link.href, { focusHeading: !link.hasAttribute('data-sb-blog-topic') });
    });

    root.sbBlogPopState = () => {
      if (navigated) load(window.location.href, { push: false });
    };
  }

  const bindWithin = (scope) => scope.querySelectorAll('[data-sb-blog]').forEach(init);

  window.addEventListener('popstate', () => {
    document.querySelectorAll('[data-sb-blog]').forEach((root) => {
      if (typeof root.sbBlogPopState === 'function') root.sbBlogPopState();
    });
  });
  document.addEventListener('shopify:section:load', (event) => bindWithin(event.target));
  bindWithin(document);
})();
